import type { AnalyticsEvents, FunnelContext } from '@sunflower-games/analytics-events';
import get from 'lodash/get';
import { action, autorun, computed, makeObservable, observable, runInAction } from 'mobx';

import { analytics } from '../../services/analytics';
import { getRequest, postRequest } from '../../services/api';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import type { ApiResponse } from '../../services/types';
import { isProduction } from '../../utils/getEnvironment';
import { devError } from '../../utils/log';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

import type {
  AnalyticsSubscriptionData,
  SubscriptionApiPayload,
  SubscriptionPlanEnhanced,
  UserSubscription,
} from './types';
import { SubscriptionUtils } from './utils';

const SUBSCRIPTION_FEATURE_FLAG = 'subscription_feat_client';

export class SubscriptionStore {
  private readonly rootStore: RootStore;
  private isFetching: boolean = false;
  private fetchPromise: Promise<boolean> | null = null;
  private funnelContext: FunnelContext | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    this.setSubscriptionEnabled(resolveFlag(SUBSCRIPTION_FEATURE_FLAG, !isProduction));

    ldClient?.on('ready', () => {
      this.setSubscriptionEnabled(resolveFlag(SUBSCRIPTION_FEATURE_FLAG, !isProduction));
    });

    ldClient?.on('change', (changes) => {
      if (SUBSCRIPTION_FEATURE_FLAG in changes) {
        this.setSubscriptionEnabled(resolveFlag(SUBSCRIPTION_FEATURE_FLAG, !isProduction));
      }
    });

    autorun(() => {
      const isUserAuthorized = this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized;
      if (isUserAuthorized) {
        this.setSubscriptionEnabled(resolveFlag(SUBSCRIPTION_FEATURE_FLAG, !isProduction));
        void this.fetch();
      } else {
        this.setSubscriptionEnabled(false);
      }
    });
  }

  @observable
  public loading: boolean = false;

  @observable
  public availablePlans: SubscriptionPlanEnhanced[] = [];

  @observable
  public currentSubscription: UserSubscription | null = null;

  @observable
  public subscriptionHistory: UserSubscription[] = [];

  @observable
  public subscriptionEnabled: boolean = false;

  @computed
  public get winsBalance(): number {
    return this.rootStore.walletStore.wallet.cash || 0;
  }

  @action
  public setSubscriptionEnabled(enabled: boolean) {
    this.subscriptionEnabled = enabled;
  }

  @action
  public setLoading(loading: boolean) {
    this.loading = loading;
  }

  @action
  public setAvailablePlans(plans: SubscriptionPlanEnhanced[]) {
    this.availablePlans = plans;
  }

  private calculateIsAvailable(subscription: UserSubscription): boolean {
    if (subscription.status === 'active' || subscription.status === 'pending') {
      return true;
    }

    if (subscription.status === 'inactive') {
      if (subscription.plan.isAvailable !== undefined) {
        return subscription.plan.isAvailable;
      }

      if (subscription.endDate) {
        const daysSinceCancellation = Math.floor(
          (Date.now() - new Date(subscription.endDate).getTime()) / (1000 * 60 * 60 * 24),
        );
        return daysSinceCancellation <= 30;
      }

      return true;
    }
    return false;
  }

  private processSubscription(subscription: UserSubscription): UserSubscription {
    return {
      ...subscription,
      plan: {
        ...subscription.plan,
        isAvailable: this.calculateIsAvailable(subscription),
      },
    };
  }

  @action
  public setCurrentSubscription(subscription: UserSubscription | null) {
    this.currentSubscription = subscription ? this.processSubscription(subscription) : null;
  }

  @action
  public setSubscriptionHistory(history: UserSubscription[]) {
    this.subscriptionHistory = history.map((sub) => this.processSubscription(sub));
  }

  @computed
  get finishedCrownPrimeWelcomeTutorial() {
    const { userTutorials } = this.rootStore.userStore.profile ?? {};
    const finishedCrownPrimeWelcomeTutorial = userTutorials?.some(
      (tutorial) => tutorial.type === 'BINGO' && tutorial.finishedAt,
    );
    return finishedCrownPrimeWelcomeTutorial;
  }

  @computed
  get hasActiveSubscription() {
    return this.currentSubscription?.status === 'active';
  }

  @computed
  get currentSubscriptionCardsCount() {
    const { nextUpcomingEventCoinType } = this.rootStore.bingoStore;
    if (!this.currentSubscription || this.currentSubscription.status !== 'active') {
      return 0;
    }

    return SubscriptionUtils.getCardsCounts(
      this.currentSubscription.plan.benefitPackage.packageBundles,
      nextUpcomingEventCoinType || 'GC',
    );
  }

  @computed
  get currentPlan(): SubscriptionPlanEnhanced | null {
    try {
      const currSubscription = this.currentSubscription;
      if (!currSubscription) return null;

      const packageBundle = currSubscription.plan?.benefitPackage?.packageBundles?.[0];
      if (!packageBundle) return null;

      return {
        packageId: currSubscription.plan.packageId,
        id: currSubscription.plan.id,
        name: currSubscription.plan.name,
        description: currSubscription.plan.description || '',
        price: currSubscription.plan.price,
        cycleType: currSubscription.plan.cycleType,
        countryCode: currSubscription.plan.countryCode,
        label: '',
        benefitPackage: currSubscription.plan.benefitPackage,
        isActive: currSubscription.plan.isActive,
        subscriptionStatus: currSubscription.status,
        type: 'subscription',
      };
    } catch (error) {
      devError('Error computing current bundle', error);
      return null;
    }
  }

  @computed
  get subscriptionStatus() {
    if (!this.currentSubscription) return 'none';
    return this.currentSubscription.status;
  }

  @computed
  get analyticsSubscriptionStatus(): 'active' | 'inactive' | 'never_bought' {
    if (!this.currentSubscription) return 'never_bought';
    if (this.currentSubscription.status === 'active') return 'active';
    if (this.currentSubscription.status === 'inactive') return 'inactive';
    return 'never_bought';
  }

  @computed
  get analyticsSubscriptionData(): AnalyticsSubscriptionData | null {
    if (!this.currentSubscription) return null;

    if (this.currentSubscription.status === 'active') {
      return {
        subscription_id: this.currentSubscription.id,
        plan_id: this.currentSubscription.plan.id,
        plan_name: this.currentSubscription.plan.name,
        plan_type: this.currentSubscription.plan.cycleType,
        next_billing_date: this.currentSubscription.nextBillingDate,
        cards_count: SubscriptionUtils.getCardsCounts(
          this.currentSubscription.plan.benefitPackage.packageBundles,
          'GC',
        ),
      };
    }

    if (this.currentSubscription.status === 'inactive') {
      return {
        subscription_id: this.currentSubscription.id,
        plan_id: this.currentSubscription.plan.id,
        plan_name: this.currentSubscription.plan.name,
        end_date: this.currentSubscription.endDate,
      };
    }

    return null;
  }

  public async fetch(options?: { background?: boolean }): Promise<boolean> {
    if (this.isFetching) {
      return this.fetchPromise ?? Promise.resolve(true);
    }

    if (!this.subscriptionEnabled) {
      return Promise.resolve(true);
    }

    this.isFetching = true;

    const promise = (async () => {
      try {
        if (!options?.background) {
          this.setLoading(true);
        }

        const response = await getRequest<ApiResponse<SubscriptionApiPayload>>('/v2/subscription/data');
        const payload = get(response, 'data.data', null);

        if (!payload) {
          devError('No subscription data received from API');
          return false;
        }

        const availablePlansFinal = payload.availablePlans
          .filter(
            (plan) =>
              plan.id === this.currentPlan?.id ||
              (plan.isActive && (!this.currentPlan || plan.price > this.currentPlan.price)),
          )
          .sort((a, b) => Number(b.price) - Number(a.price));

        runInAction(() => {
          this.setAvailablePlans(availablePlansFinal);
          this.setCurrentSubscription(payload.activeSubscription);
          this.setSubscriptionHistory(payload.subscriptionHistory);
        });

        return true;
      } catch (error) {
        devError('Error fetching subscription data', error);
        return false;
      } finally {
        if (!options?.background) {
          this.setLoading(false);
        }
        this.isFetching = false;
        this.fetchPromise = null;
      }
    })();

    this.fetchPromise = promise;
    return promise;
  }

  public async cancelSubscription(): Promise<boolean> {
    if (!this.currentPlan) {
      devError('Cannot cancel plan: no current plan found');
      return false;
    }

    if (!this.subscriptionEnabled) {
      devError('Cannot cancel plan: subscriptions feature disabled');
      return false;
    }

    try {
      this.setLoading(true);

      const response = await postRequest<ApiResponse<null>>('/v2/subscription/cancel');

      if (response.data.success) {
        runInAction(() => {
          if (this.currentSubscription) {
            this.currentSubscription.status = 'inactive';
            this.currentSubscription.endDate = this.currentSubscription.nextBillingDate;
          }
        });

        // Refresh data but don't fail if it errors
        try {
          await this.fetch({ background: true });
        } catch (fetchError) {
          devError('Error refreshing data after cancellation (non-critical)', fetchError);
        }

        return true;
      } else {
        devError('Subscription cancellation failed: API returned unsuccessful response', response);
        return false;
      }
    } catch (error) {
      devError('Error cancelling subscription', error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  public openCancelSubscriptionModal() {
    if (!this.subscriptionEnabled) {
      return;
    }
    this.rootStore.uiStore.openDialog = {
      dialog: EnumOpenDialog.isOpenCancelSubscriptionDialog,
      options: {},
    };
  }

  public closeCancelSubscriptionModal() {
    this.rootStore.uiStore.openDialog = {
      dialog: null,
      options: {},
    };
  }

  @action
  public resetAll() {
    this.setSubscriptionEnabled(false);
    this.setLoading(false);
    this.setAvailablePlans([]);
    this.setCurrentSubscription(null);
    this.setSubscriptionHistory([]);
    this.isFetching = false;
    this.fetchPromise = null;
    this.funnelContext = null;
  }

  public initializeFunnel(params: {
    funnelType: 'purchase' | 'cancel' | 'redeem';
    entrySourceScreen: string;
    entryTrigger: string;
    funnelStartStep: string;
    entryPoint?: string;
  }): FunnelContext {
    const { funnelType, entrySourceScreen, entryTrigger, funnelStartStep, entryPoint } = params;

    this.funnelContext = {
      funnel_type: funnelType,
      funnel_session_id: `${funnelType}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      entry_source_screen: entrySourceScreen,
      entry_trigger: entryTrigger,
      funnel_start_step: funnelStartStep,
      current_funnel_step: funnelStartStep,
      ...(entryPoint && { entry_point: entryPoint }),
    };
    return this.funnelContext;
  }

  public getFunnelContext(): FunnelContext | null {
    return this.funnelContext;
  }

  @action
  public updateFunnelStep(step: string) {
    if (this.funnelContext) {
      this.funnelContext.current_funnel_step = step;
    }
  }

  public clearFunnelContext() {
    this.funnelContext = null;
  }

  public sendAnalyticsEvent<K extends keyof AnalyticsEvents>(eventName: K, params: AnalyticsEvents[K]) {
    void analytics.sendEvent(eventName, {
      ...params,
      user_subscription_status: this.analyticsSubscriptionStatus,
      ...(this.analyticsSubscriptionData && { current_active_subscription: this.analyticsSubscriptionData }),
    });
  }
}
